/**
 * Formulas worked out in the browser, e.g. `[Price] * first([Future Volumes], [Current Volumes])`.
 *
 * Used by a text's Formula and by an input widget's Value formula (a slider or
 * input box whose value is calculated). A `[Name]` is another layer, looked up
 * by its layer name (case, spaces and punctuation ignored):
 *
 * - an input box / slider → what was typed or slid (or its own value formula);
 * - a text bound to data → its live value;
 * - a text with a formula → that result;
 * - a plain text → the number typed in it.
 *
 * An input with nothing in it is EMPTY. Empty flows through the maths (any sum
 * with an empty part is empty and shows "—"), and `first(a, b, …)` picks the
 * first part that is not empty — so `first([Future Volumes], [Current Volumes])`
 * means "Future when it is filled in, else Current".
 *
 * The expression is parsed by hand — never `eval` — and supports
 * `+ - * / %`, `^` (power), brackets, unary minus, comparisons
 * `> < >= <= == !=` (1 = true, 0 = false) and the functions in {@link FUNCTIONS}.
 */

import type { DesignNode, TextDataBinding } from '../design-builder.types';
import { parseNumber } from './design-slide.utils';

/** Appearance key holding an input widget's value formula. */
export const VALUE_FORMULA_KEY = 'valueFormula';

export type FormulaResult =
  | { status: 'ready'; value: number }
  /** A referenced input is still empty — show a dash, not an error. */
  | { status: 'empty' }
  | { status: 'error'; message: string };

export interface FormulaContext {
  /** Every layer the formula may reference. */
  nodes: readonly DesignNode[];
  /** A bound text's live value, or null while it loads. */
  readBound: (binding: TextDataBinding) => number | string | null;
  /** What was typed into an input widget, or null when nothing yet. */
  readInput: (node: DesignNode) => number | string | null;
}

/** An input widget's value formula, or '' when it has none. */
export function valueFormulaOf(node: DesignNode): string {
  const formula = node.widget?.appearance?.[VALUE_FORMULA_KEY];
  return typeof formula === 'string' ? formula : '';
}

/** Names match ignoring case, spaces and punctuation: `[Current Volumes]` finds "CurrentVolumes". */
const keyOf = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Inside the parser an empty value is NaN, so it carries through the maths. */
const EMPTY = Number.NaN;

export function evaluateFormula(expression: string, context: FormulaContext, seen: ReadonlySet<string> = new Set()): FormulaResult {
  if (!expression.trim()) return { status: 'empty' };
  const byName = new Map<string, DesignNode>();
  for (const node of context.nodes) if (!byName.has(keyOf(node.name))) byName.set(keyOf(node.name), node);

  const resolve = (name: string): number => {
    const node = byName.get(keyOf(name));
    if (!node) throw new FormulaError(`No layer named "${name}".`);
    const value = nodeValue(node, context, seen);
    if (value.status === 'error') throw new FormulaError(`${name}: ${value.message}`);
    return value.status === 'empty' ? EMPTY : value.value;
  };

  try {
    const value = new Parser(expression, resolve).parse();
    if (Number.isNaN(value)) return { status: 'empty' };
    if (!Number.isFinite(value)) return { status: 'error', message: 'Result is not a number (divide by zero?).' };
    return { status: 'ready', value };
  } catch (error) {
    return { status: 'error', message: error instanceof FormulaError ? error.message : 'Invalid formula.' };
  }
}

/**
 * A formula whose `[Name]`s are looked up by the caller (null = empty), e.g. a
 * zone slider's margin formula with `[Value]` = the handle being dragged.
 */
export function evaluateExpression(expression: string, resolve: (name: string) => number | null): FormulaResult {
  if (!expression.trim()) return { status: 'empty' };
  try {
    const value = new Parser(expression, (name) => resolve(name) ?? EMPTY).parse();
    if (Number.isNaN(value)) return { status: 'empty' };
    if (!Number.isFinite(value)) return { status: 'error', message: 'Result is not a number (divide by zero?).' };
    return { status: 'ready', value };
  } catch (error) {
    return { status: 'error', message: error instanceof FormulaError ? error.message : 'Invalid formula.' };
  }
}

/** The number one referenced layer stands for. */
function nodeValue(node: DesignNode, context: FormulaContext, seen: ReadonlySet<string>): FormulaResult {
  const own = node.type === 'WIDGET' ? valueFormulaOf(node) : node.type === 'TEXT' ? node.formula?.expression ?? '' : '';
  if (own.trim()) {
    if (seen.has(node.id)) return { status: 'error', message: 'formula refers back to itself.' };
    return evaluateFormula(own, context, new Set([...seen, node.id]));
  }
  if (node.type === 'WIDGET') return toResult(context.readInput(node));
  if (node.type !== 'TEXT') return { status: 'error', message: 'only texts and inputs can be used.' };
  if (node.dataBinding) return toResult(context.readBound(node.dataBinding));
  const typed = parseNumber(node.text?.value ?? '');
  return typed === null ? { status: 'error', message: 'has no number in it.' } : { status: 'ready', value: typed };
}

function toResult(value: number | string | null | undefined): FormulaResult {
  if (value === null || value === undefined || String(value).trim() === '') return { status: 'empty' };
  const numeric = typeof value === 'number' ? value : parseNumber(String(value));
  return numeric === null ? { status: 'error', message: `"${value}" is not a number.` } : { status: 'ready', value: numeric };
}

class FormulaError extends Error {}

const isEmpty = (value: number) => Number.isNaN(value);
const bool = (value: boolean) => (value ? 1 : 0);

/** Function names are case-insensitive. Shown to authors in the inspector hints. */
const FUNCTIONS: Record<string, (args: number[]) => number> = {
  /** The first argument that is not empty or 0 — "Future if filled in, else Current". */
  first: (args) => args.find((value) => !isEmpty(value) && value !== 0) ?? args.find((value) => !isEmpty(value)) ?? EMPTY,
  /** `if(condition, then, else)` — the condition is true when it is not 0. */
  if: ([condition, then, otherwise = EMPTY]) => (isEmpty(condition) ? EMPTY : condition !== 0 ? then : otherwise),
  /** 1 when the argument is empty, else 0. */
  isempty: ([value]) => bool(isEmpty(value)),
  min: (args) => Math.min(...args),
  max: (args) => Math.max(...args),
  abs: ([x]) => Math.abs(x),
  floor: ([x]) => Math.floor(x),
  ceil: ([x]) => Math.ceil(x),
  sqrt: ([x]) => Math.sqrt(x),
  round: ([x, digits = 0]) => {
    const factor = 10 ** Math.round(digits);
    return Math.round(x * factor) / factor;
  },
};

const COMPARISONS: Record<string, (a: number, b: number) => boolean> = {
  '>=': (a, b) => a >= b,
  '<=': (a, b) => a <= b,
  '==': (a, b) => a === b,
  '!=': (a, b) => a !== b,
  '>': (a, b) => a > b,
  '<': (a, b) => a < b,
};

/**
 * Recursive descent, lowest precedence first:
 *   compare := expr (('>=' | '<=' | '==' | '!=' | '>' | '<') expr)?
 *   expr    := term (('+' | '-') term)*
 *   term    := power (('*' | '/' | '%') power)*
 *   power   := unary ('^' power)?
 *   unary   := '-' unary | '+' unary | atom
 *   atom    := number | [Name] | name '(' args ')' | '(' compare ')'
 */
class Parser {
  private pos = 0;

  constructor(private readonly src: string, private readonly resolve: (name: string) => number) {}

  parse(): number {
    const value = this.compare();
    this.skip();
    if (this.pos < this.src.length) throw new FormulaError(`Unexpected "${this.src[this.pos]}".`);
    return value;
  }

  private compare(): number {
    const left = this.expr();
    this.skip();
    const op = Object.keys(COMPARISONS).find((candidate) => this.src.startsWith(candidate, this.pos));
    if (!op) return left;
    this.pos += op.length;
    const right = this.expr();
    // Comparing with an empty input is itself empty, not false.
    return isEmpty(left) || isEmpty(right) ? EMPTY : bool(COMPARISONS[op](left, right));
  }

  private expr(): number {
    let value = this.term();
    for (;;) {
      if (this.eat('+')) value += this.term();
      else if (this.eat('-')) value -= this.term();
      else return value;
    }
  }

  private term(): number {
    let value = this.power();
    for (;;) {
      if (this.eat('*')) value *= this.power();
      else if (this.eat('/')) value /= this.power();
      else if (this.eat('%')) value %= this.power();
      else return value;
    }
  }

  private power(): number {
    const base = this.unary();
    return this.eat('^') ? base ** this.power() : base;
  }

  private unary(): number {
    if (this.eat('-')) return -this.unary();
    if (this.eat('+')) return this.unary();
    return this.atom();
  }

  private atom(): number {
    this.skip();
    const rest = this.src.slice(this.pos);

    if (this.eat('(')) {
      const value = this.compare();
      this.expect(')');
      return value;
    }
    if (this.eat('[')) {
      const end = this.src.indexOf(']', this.pos);
      if (end < 0) throw new FormulaError('Missing "]".');
      const name = this.src.slice(this.pos, end);
      this.pos = end + 1;
      return this.resolve(name);
    }
    const number = rest.match(/^(\d+(?:\.\d*)?|\.\d+)/);
    if (number) {
      this.pos += number[0].length;
      return Number(number[0]);
    }
    const name = rest.match(/^[a-zA-Z_]\w*/);
    if (name) {
      const fn = FUNCTIONS[name[0].toLowerCase()];
      if (!fn) throw new FormulaError(`Unknown "${name[0]}" — put layer names in [brackets].`);
      this.pos += name[0].length;
      this.expect('(');
      const args = [this.compare()];
      while (this.eat(',')) args.push(this.compare());
      this.expect(')');
      return fn(args);
    }
    throw new FormulaError(this.pos >= this.src.length ? 'Formula ends too early.' : `Unexpected "${this.src[this.pos]}".`);
  }

  private skip(): void {
    while (this.pos < this.src.length && /\s/.test(this.src[this.pos])) this.pos++;
  }

  private eat(char: string): boolean {
    this.skip();
    if (this.src[this.pos] !== char) return false;
    this.pos++;
    return true;
  }

  private expect(char: string): void {
    if (!this.eat(char)) throw new FormulaError(`Missing "${char}".`);
  }
}
